// @ts-strict-ignore
import { Component, effect, ElementRef, OnDestroy, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { RefresherCustomEvent } from "@ionic/angular";
import { Subject } from "rxjs";
import { NavigationService } from "src/app/shared/components/navigation/service/navigation.service";
import { DataService } from "src/app/shared/components/shared/dataservice";
import { getEdgeUiwidgetRequest } from "src/app/shared/jsonrpc/request/getEdgeUiwidgetRequest";
import { QueryHistoricTimeseriesEnergyRequest } from "src/app/shared/jsonrpc/request/queryHistoricTimeseriesEnergyRequest";
import { getEdgeUiwidgetResponse } from "src/app/shared/jsonrpc/response/getEdgeUiwidgetResponse";
import { QueryHistoricTimeseriesEnergyResponse } from "src/app/shared/jsonrpc/response/queryHistoricTimeseriesEnergyResponse";
import { UserService } from "src/app/shared/service/user.service";
import { ChannelAddress, Edge, EdgeConfig, EdgePermission, Service, Utils, Websocket } from "src/app/shared/shared";
import { Widgets } from "src/app/shared/type/widgets";
import { DateTimeUtils } from "src/app/shared/utils/datetime/datetime-utils";
@Component({
    selector: "signage",
    templateUrl: "./signage.component.html",
    standalone: false,
})
export class SignageComponent implements OnDestroy {

    @ViewChild("modal", { read: ElementRef }) public modal!: ElementRef;

    public edge: Edge | null = null;
    public config: EdgeConfig | null = null;
    public widgets: Widgets | null = null;

    public imageData0: string = "";
    public imageTitle0: string = "";
    public imageData1: string = "";
    public imageTitle1: string = "";
    public curMonthPro: number | undefined;
    public lastMonthPro: number | undefined;
    public currentYearPro: number | undefined;
    public sensor: boolean = false;

    protected isModbusTcpWidgetAllowed: boolean = false;
    protected showRefreshDragDown: boolean = false;
    protected showNewFooter: boolean = false;

    private stopOnDestroy: Subject<void> = new Subject<void>();
    private interval: ReturnType<typeof setInterval> | undefined;
    private lastLoadedEdgeId: string | null = null;

    constructor(
        private route: ActivatedRoute,
        public service: Service,
        protected utils: Utils,
        protected websocket: Websocket,
        public router: Router,
        private dataService: DataService,
        protected navigationService: NavigationService,
        private userService: UserService,
    ) {
        // Set up the main effect to react to edge changes
        effect(() => {
            const edge = this.service.currentEdge();
            this.edge = edge;
            if (edge) {
                this.isModbusTcpWidgetAllowed = EdgePermission.isModbusTcpApiWidgetAllowed(edge);
                this.loadConfigAndData(edge);
            } else {
                this.isModbusTcpWidgetAllowed = false;
            }
        });
    }


    public ionViewWillEnter() {
        if (this.widgets?.list) {
            this.showNewFooter = this.widgets.list.some(item => item.name == "Evse.Controller.Single" || item.name == "Controller.IO.Heating.Room");
        }
        this.checkIfRefreshNeeded();
    }

    public ionViewWillLeave() {
        this.ngOnDestroy();
    }

    public ngOnDestroy() {
        clearInterval(this.interval);
        this.stopOnDestroy.next();
        this.stopOnDestroy.complete();
        this.lastLoadedEdgeId = null; // Reset for next time component is initialized
    }

    protected handleRefresh: (ev: RefresherCustomEvent) => void = (ev: RefresherCustomEvent) => this.dataService.refresh(ev);

    protected checkIfRefreshNeeded() {
        this.interval = setInterval(async () => {
            if (this.edge?.isOnline === false) {
                this.showRefreshDragDown = false;
                return;
            }
            const lastUpdate: Date | null = this.dataService.lastUpdated();
            if (lastUpdate == null) {
                this.showRefreshDragDown = true;
                return;
            }
            this.showRefreshDragDown = DateTimeUtils.isDifferenceInSecondsGreaterThan(20, new Date(), lastUpdate);
        }, 5000);
    }

    private loadConfigAndData(edge: Edge) {
        // Prevent re-loading data for the same edge
        if (this.lastLoadedEdgeId === edge.id) {
            return;
        }
        this.lastLoadedEdgeId = edge.id;

        // Load config and then signage-specific data
        this.service.getConfig().then(config => {
            this.config = config;
            this.widgets = config.widgets;
            this.sensor = Object.values(config.components).some(c => /^sensor[0-9]*$/.test(c.id));

            // Sort the Storage widget
            if (this.widgets?.classes) {
                this.widgets.classes.sort((a, b) => {
                    if (a === "Storage") {
                        return 1;
                    }
                    if (b === "Storage") {
                        return -1;
                    }
                    return 0;
                });
            }

            // Load all signage specific data
            this.loadSignageData(edge);
        });
    }

    private loadSignageData(edge: Edge) {
        const edgeId = edge.id;
        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const firstDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
        const energyChannels = [ChannelAddress.fromString("_sum/ProductionActiveEnergy")];

        // Create all request objects
        const imageRequest = new getEdgeUiwidgetRequest({ edgeId });
        const currentYearRequest = new QueryHistoricTimeseriesEnergyRequest(firstDayOfYear, today, energyChannels);
        const lastMonthRequest = new QueryHistoricTimeseriesEnergyRequest(firstDayOfPreviousMonth, lastDayOfPreviousMonth, energyChannels);
        const curMonthRequest = new QueryHistoricTimeseriesEnergyRequest(firstDayOfMonth, today, energyChannels);

        // Execute all requests in parallel
        Promise.all([
            this.websocket.sendSafeRequest(imageRequest),
            edge.sendRequest(this.websocket, currentYearRequest),
            edge.sendRequest(this.websocket, lastMonthRequest),
            edge.sendRequest(this.websocket, curMonthRequest),
        ]).then(([imageResponse, yearResponse, lastMonthResponse, curMonthResponse]) => {
            // Process image response
            const metDdata = (imageResponse as getEdgeUiwidgetResponse).result.edge;
            this.imageData0 = metDdata?.[0]?.image ? "data:image/svg+xml;base64," + metDdata[0].image : "";
            this.imageTitle0 = metDdata?.[0]?.title ? metDdata[0].title : "";
            this.imageData1 = metDdata?.[1]?.image ? "data:image/svg+xml;base64," + metDdata[1].image : "";
            this.imageTitle1 = metDdata?.[1]?.title ? metDdata[1].title : "";

            // Process energy responses
            this.currentYearPro = (yearResponse as QueryHistoricTimeseriesEnergyResponse).result.data["_sum/ProductionActiveEnergy"];
            this.lastMonthPro = (lastMonthResponse as QueryHistoricTimeseriesEnergyResponse).result.data["_sum/ProductionActiveEnergy"];
            this.curMonthPro = (curMonthResponse as QueryHistoricTimeseriesEnergyResponse).result.data["_sum/ProductionActiveEnergy"];

        }).catch(error => {
            console.error("Error loading signage data:", error);
            // Reset data on error
            this.imageData0 = "";
            this.imageTitle0 = "";
            this.imageData1 = "";
            this.imageTitle1 = "";
            this.currentYearPro = undefined;
            this.lastMonthPro = undefined;
            this.curMonthPro = undefined;
        });
    }
}
